// @flow
import React, { useState, useEffect, useRef } from 'react';
import type { Element } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { connect } from 'react-redux';
import { NextSeo } from 'next-seo';
import ItemsCarousel from 'react-items-carousel';

import waitForPageReady from '@lib/waitForPageReady';
import compose from 'lodash/fp/compose';

import {
  YAPITA_HEALTH_URL, DETAILS_FACILITY, CUREFY_URL, FINDER_PACKAGE
} from '@routes';
import { useBreakpoint } from '@hooks';

import type { Doctors } from '@screens/Finder/Doctor/types';
import GridCarousel from '@components/GridCarousel/index';
import {
  Link, withTranslation, nextRoutes
} from '@i18n';
import { withFormik } from '@lib';
import ProgressBar from '@components/ContributionCard/ProgressBar';

import { changeTitle } from '@redux/components/Metadata/actions';
import { requestDoctorList, resetDoctorList } from '@screens/Finder/Doctor/redux/actions';
import { requestPackageList } from '@screens/Finder/Package/redux/actions';
import PackageSearchResultListItem from '@screens/Finder/Package/SearchResultList/SearchResultListItem/index';
import { calculateGridUnitSize } from '@lib/helpers';
import MiniFlow from '@screens/HowItWorks/components/MiniFlow/index';
import SearchResultListItem from '@screens/Finder/Doctor/SearchResultList/SearchResultListItem/index';
import type { GlobalState } from '@globalTypes';
import { currencySign } from '@lib/flowConstant';
import ConsultationForm from './Form';

import {
  StyledCover,
  StyledWrapper,
  StyledContentBlock,
  ImageBlock,
  DoctorName,
  DoctorPost,
  Heading,
  DoctorDetailsBlock,
  DoctorValues,
  StyledBlock,
  DoctorSection,
  DoctorImage,
  StyledContentBox,
  ContentBlock,
  DoctorLeftBlock,
  Procedures,
  Item,
  DoctorProcedures,
  HighlightsBlock,
  DoctorFacilityBlock,
  DoctorHeading,
  FacilityBlock,
  FacilityName,
  FacilityContentBlock,
  Content,
  FacilityImg,
  FacilityImage,
  StyledList,
  StyledListItem,
  FacilityLocation,
  Location,
  LocationIconImg,
  SeeMoreButton,
  HiddenStyledForm,
  FormMini,
  HighlightsBlockContent,
  StyledCoverMaxWidthNoBottomMargin,
  StyledArrowWrapper,
  MainSubHealine,
  Loader,
  FacilityLocationBlock,
  HighlightContentBlock,
  FacilityLink,
  DoctorImg,
  HiddenBlock,
  StyledBulletListWrapper,
  StyledRichTextContent,
  StyledDoctorInfo,
  StyledContentDiv,
  Hospital,
  LocationImage,
  DepartmentImage,
  DoctorIcon,
  PlayImage,
  StyledPlay,
  StyledPlan,
  StyledBook,
  StyledTreatmentText,
  StyledVideoIcon,
  StyledVideoAppointment,
  StyledVideoBookNow,
  Styledform,
  FacilityLocationMobile,
  StyledDoctorInfoMobile,
  DoctorAttributes,
  StyledContentHeading,
  StyledHorizontalLine,
  StyledMobileView,
  ProceduresMobileView,
  StyledSeeMore,
  StyledFlexCenter,
  StyledPackagesMobile
} from './styles';
import DoctorPackages from './DoctorPackages';
import { requestDoctorDetails, resetDoctorDetails } from './redux/actions';
import type {
  Props, Facility, Doctor, StaticMetaData
} from './types';

const DoctorDetails = ({
  t,
  resetDoctorDetails,
  doctorDetails,
  requestDoctorList,
  requestPackageList,
  packages,
  resetDoctorList,
  doctorFinder,
  curefyDomain,
  changeTitle,
}: Props): Element<any> => {
  const doctor: ?Doctor = doctorDetails.data;
  const isMobile: boolean = useBreakpoint('mobile', 'tablet');
  const isTablet: boolean = useBreakpoint('tablet', 'desktop');
  const { locale } = nextRoutes;
  const title: ?string = doctor?.attributes?.name;
  const [doctorsSlide, setDoctorsSlide] = React.useState(0);
  const [packageSlide, setPackageSlide] = React.useState(0);
  // eslint-disable-next-line no-nested-ternary
  const onScreenCountDoctors = isMobile ? 1 : isTablet ? 3 : 4;
  const [isNeedToloadCards, setIsNeedToloadCards] = React.useState(false);
  const [isCardsApiLoaded, setCardsApiLoaded] = useState(false);
  // const [scrollDoctorRight, setScrollDoctorRight] = useState(false);

  useEffect((): Function => resetDoctorDetails, []);

  useEffect((): void => {
    changeTitle({
      IN: {
        en: `${doctor?.attributes?.name || ''}, ${doctor?.attributes?.speciality || ''} in ${doctor
          ?.attributes?.address_city || ''}, ${t('Doctors:AppointmentTitle')}`
      }
    });
  }, [doctor?.attributes?.name]);

  const facility: ?Facility = doctor?.relationships?.facility?.data;

  const highlights: Array<string> = (doctor?.attributes?.highlights || '').split('\n');
  // .map(stringToParagraph);

  const departments: string = doctor?.attributes?.department || '';

  const city: string = doctor?.attributes?.address_city || '';

  const qualificationAndExp = doctor?.attributes?.qualification_and_experience;

  const qualification = qualificationAndExp ? qualificationAndExp.trimStart() : '';

  const photoUrl: string = facility?.relationships?.photos?.data?.[0]?.attributes?.photo_url
    || '/images/finder/hospitals.jpg';
  const doctor_id = doctor?.attributes?.id;

  useEffect(() => {
    if (isNeedToloadCards && !isCardsApiLoaded) {
      requestDoctorList({
        department: departments,
        address_city: city,
        dontScroll: true,
        custom_per_page: 10
      });
      setCardsApiLoaded(true);
    }
  }, [isNeedToloadCards]);

  useEffect(() => {
    requestPackageList({ doctor_id });
  }, []);

  const doctors: ?Doctors = (doctorFinder.data || []).slice(0, 8).filter(
    doctor => doctor.id !== doctor_id
  );
  useEffect((): Function => {
    const observer = new IntersectionObserver(([entry]) =>
      setIsNeedToloadCards(entry.isIntersecting));

    if (doctorLoadRef.current) {
      observer.observe(doctorLoadRef.current);
    }

    return () => {
      resetDoctorList();
      observer.disconnect();
    };
  }, []);

  const experience = (): string => {
    const dob = new Date(doctor?.attributes?.practicing_from || '');
    const month_diff = Date.now() - dob.getTime();
    const age_dt: Date = new Date(month_diff);
    const year = age_dt.getUTCFullYear();
    return Math.abs(year - 1970).toString();
  };

  // const descriptionBullets: Array<string> =
  // (facility?.attributes?.description || '').split('\n');
  const textOverview = facility?.attributes?.description;
  // eslint-disable-next-line no-unused-vars
  const [showFullContent, setShowFullContent] = useState(false);

  const transformedString = departments
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  const treatment_names = doctor?.attributes?.treatment_names || [];
  const treatment_ids = doctor?.attributes?.treatment_ids || [];

  const doctorLoadRef = useRef(null);
  // const facilityslug = facility?.attributes?.slug ? `${facility?.attributes?.slug || ''}` :
  const metaData: StaticMetaData = t('Seo:DoctorSeo', {
    returnObjects: true,
    name: doctor?.attributes?.name,
    department: t(`Doctors:departments.${doctor?.attributes?.department || ''}`),
    address_city: doctor?.attributes?.address_city,
    address_country: doctor?.relationships?.facility?.data?.attributes?.address_country
  });
  const [loadedCurrency, setloadedCurrency] = useState('INR');

  useEffect((): void => {
    fetch('https://ipapi.co/json')
      .then(res => res.json())
      .then((response) => {
        // eslint-disable-next-line no-unused-expressions
        if (response.currency && ['USD', 'EUR', 'JPY', 'INR',
          'GBP', 'POUND'].includes(response.currency)) {
          setloadedCurrency(response.currency);
        }
      })
      .catch(() => { });
    // if (myRef && pathname && pathname !== '/') { executeScroll(); }
  }, []);

  const consultation_fees = doctor?.relationships?.consultation_fees?.data;
  const planFees = consultation_fees && consultation_fees.find(
    fees => fees.attributes.currency === loadedCurrency
  );


  const [intervalPackageId, setPackageIntervalId] = React.useState(null);
  const color = '#5FA6BF';
  const handlePackageTimeInterval = () => {
    clearInterval(intervalPackageId);
    const newId = setInterval(() => {
      setPackageSlide((x) => {
        if (x > (packages || []).slice(0, 8).length - 1) {
          return 0;
        }
        return x + 1;
      });
    }, 4000);

    setPackageIntervalId(newId);
  };

  const [intervalDoctorId, setDoctorIntervalId] = React.useState(null);

  const handleDoctorTimeInterval = () => {
    clearInterval(intervalDoctorId);
    const newId = setInterval(() => {
      setDoctorsSlide((x) => {
        if (x > (doctors || []).slice(0, 8).length - 1) {
          return 0;
        }
        return x + 1;
      });
    }, 4000);

    setDoctorIntervalId(newId);
  };
  return (
    <>
      <NextSeo
        title={`${doctor?.attributes?.meta_title || metaData.MetaTitle} - ${curefyDomain ? 'Curefy' : 'Yapita Health'}`}
        description={doctor?.attributes?.meta_description || metaData.MetaDescription}
        canonical={`${curefyDomain ? CUREFY_URL : YAPITA_HEALTH_URL}/${locale}/doctors/${doctor?.attributes?.slug || ''}`}
        openGraph={{
          url: `${curefyDomain ? CUREFY_URL : YAPITA_HEALTH_URL}/${locale}/doctors/${doctor?.attributes?.slug || ''}`,
          title: doctor?.attributes?.slug,
          description: doctor?.attributes?.meta_description,
          images: [
            {
              url:
                doctor?.attributes?.seo_image?.url
                || 'https://yapita-production.s3.ap-south-1.amazonaws.com/images/yapita-health-og.jpg',
              width: 800,
              height: 600,
              alt: 'Yapita Health - Your Medical Partner',
              type: 'image/jpg'
            }
          ],
          siteName: curefyDomain ? 'Curefy' : 'Yapita Health'
        }}
        twitter={{
          handle: '@yapitahealth',
          site: '@yapitahealth',
          cardType: 'summary_large_image'
        }}
      />

      <StyledCover>
        <StyledWrapper>
          <StyledContentBlock>
            <ImageBlock>
              <DoctorImage>
                <DoctorImg
                  src={
                    doctor?.attributes?.image_url
                    || (doctor?.attributes?.gender === 'male'
                      ? '/images/finder/doctor.png'
                      : '/images/finder/female-doctor.png')
                  }
                  alt={title}
                />
                {doctor?.relationships?.facility?.data?.attributes?.video_url
                  ? (
                    <StyledPlay>
                      <PlayImage />
                      {t('Doctors:Play')}
                    </StyledPlay>
                 )
                  : ''}
              </DoctorImage>
            </ImageBlock>

            <DoctorDetailsBlock>
              <DoctorName domain={curefyDomain}>{title}</DoctorName>
              <StyledDoctorInfo>
                {doctor?.attributes?.job_role
                  ? (
                    <>
                      <DoctorPost>{doctor?.attributes?.job_role}</DoctorPost>
                      <DoctorPost>•</DoctorPost>
                    </>
                    )
                  : <></>}
                {doctor?.attributes?.practicing_from
                  ? (
                    <>
                      <DoctorPost>{`${experience()}${t('DetailsJob:YearExperience')}`}</DoctorPost>
                      <DoctorPost>•</DoctorPost>
                    </>
                    )
                  : <></>}
                <DoctorPost style={{ textTransform: 'capitalize' }}>{doctor?.attributes?.gender}</DoctorPost>
              </StyledDoctorInfo>
              {doctor?.attributes?.job_role
                ? (
                  <StyledDoctorInfoMobile>
                    <DoctorPost>{doctor?.attributes?.job_role}</DoctorPost>
                  </StyledDoctorInfoMobile>
                  )
                : <></>}
              <StyledDoctorInfoMobile>
                <DoctorPost>•</DoctorPost>
                <DoctorPost>{`${experience()}${t('DetailsJob:YearExperience')}`}</DoctorPost>
                <DoctorPost>•</DoctorPost>
                <DoctorPost>{doctor?.attributes?.gender}</DoctorPost>
              </StyledDoctorInfoMobile>
              <StyledContentBox>
                <StyledContentDiv>
                  <DoctorValues>
                    <DoctorIcon />
                    <DoctorAttributes>
                      {doctor?.attributes?.speciality}
                    </DoctorAttributes>
                  </DoctorValues>
                  <DoctorValues>
                    <LocationImage />
                    <DoctorAttributes>
                      {doctor?.attributes?.address_city}
                    </DoctorAttributes>
                  </DoctorValues>
                </StyledContentDiv>

                <StyledContentDiv>
                  <DoctorValues>
                    <DepartmentImage />
                    <DoctorAttributes>
                      {transformedString}
                    </DoctorAttributes>
                  </DoctorValues>
                  <DoctorValues>
                    <Hospital />
                    <DoctorAttributes>
                      {`${facility?.attributes?.name || ''}`}
                    </DoctorAttributes>
                  </DoctorValues>
                </StyledContentDiv>
              </StyledContentBox>

              {doctor?.relationships?.consultation_fees?.data.length !== 0
                ? (
                  <StyledVideoAppointment>
                    {t('Doctors:videoAppointment')}
                    <StyledVideoBookNow
                      domain={curefyDomain}
                      onClick={() => window.open(
                        `/en/consultation-flow?doctor_id=${doctor?.attributes?.id || ''}`
                      )}
                    >
                      {t('Doctors:BookNow')}
                    </StyledVideoBookNow>
                  </StyledVideoAppointment>
                  ) : (<></>)}

              {(() => {
                if (curefyDomain) {
                  return <></>;
                }
                return treatment_names.length > 0 ? (
                  <Procedures>
                    {treatment_names.map((item, index) => (
                      <Item
                        onClick={() => window.open(
                          `https://yapitahealth.com/en/treatments/${treatment_ids[index]
                          }`,
                          '_blank',
                        )}
                      >
                        {item.replace(',', '')}
                      </Item>
                    ))}
                  </Procedures>
                ) : (
                  <></>
                );
              })()}
            </DoctorDetailsBlock>
          </StyledContentBlock>

          <StyledMobileView>
            <StyledContentHeading>
              {t('DetailsJob:Department')}
            </StyledContentHeading>
            <DoctorValues>
              <DoctorIcon />
              <DoctorAttributes>
                {doctor?.attributes?.speciality}
              </DoctorAttributes>
            </DoctorValues>
            <DoctorValues>
              <DepartmentImage />
              <DoctorAttributes>
                {transformedString}
              </DoctorAttributes>
            </DoctorValues>
            <StyledHorizontalLine />
            <StyledContentHeading>
              {t('DetailsJob:Hospitals&Locations')}
            </StyledContentHeading>
            <DoctorValues>
              <Hospital />
              <DoctorAttributes>
                {`${facility?.attributes?.name || ''}`}
              </DoctorAttributes>
            </DoctorValues>
            <DoctorValues>
              <LocationImage />
              <DoctorAttributes>
                {doctor?.attributes?.address_city}
              </DoctorAttributes>
            </DoctorValues>
          </StyledMobileView>

          {(() => {
            if (curefyDomain) {
              return <></>;
            }
            return treatment_names.length > 0 ? (
              <StyledMobileView>
                <StyledContentHeading>
                  {t('Procedures')}
                </StyledContentHeading>
                <ProceduresMobileView>
                  {treatment_names.map((item, index) => (
                    <Item
                      onClick={() => window.open(
                        `https://yapitahealth.com/en/treatments/${treatment_ids[index]
                        }`,
                        '_blank',
                      )}
                    >
                      {item.replace(',', '')}
                    </Item>
                  ))}
                </ProceduresMobileView>
              </StyledMobileView>
            ) : (
              <></>
            );
          })()}
          {qualification ? (
            <DoctorSection>
              <Heading>{t('DetailsJob:Qualifications')}</Heading>
              <ContentBlock
                dangerouslySetInnerHTML={{ __html: qualification }}
                domain={curefyDomain}
              />
            </DoctorSection>
          ) : (
            <></>
          )}
          <StyledBlock>
            <DoctorLeftBlock block={doctor?.relationships?.consultation_fees?.data.length !== 0}>
              <HiddenStyledForm>
                {doctor?.relationships?.consultation_fees?.data.length !== 0
                  ? (
                    <ConsultationForm
                      treatmentPage={title}
                      formId="mobile"
                      isMobile={isMobile}
                      formName="DoctorForm"
                      id="form"
                      doctor_id={doctor_id}
                      department={doctor?.attributes?.department}
                      booking_currency={loadedCurrency}
                      booking_category="second-opinion"
                      curefyDomain={curefyDomain}
                    />
                   )
                  : (
                    <MiniFlow
                      doctor={typeof window !== 'undefined' && window?.location?.href}
                      treatmentPage={title}
                      formId="mobile"
                      isMobile={isMobile}
                      formName="DoctorForm"
                      id="form"
                      curefyDomain={curefyDomain}
                    />
                    )}
              </HiddenStyledForm>

              {doctor?.relationships?.consultation_fees?.data.length !== 0 ? (
                <DoctorProcedures>
                  <Heading>
                    {t('Doctors:doctorConsultation')}
                  </Heading>
                  <StyledPlan>
                    <StyledTreatmentText>
                      {t('Doctors:OnlineConsultation', {
                        currency: currencySign[planFees ? planFees.attributes.currency : 'INR'],
                        consultationTreatmentPlan: planFees ? planFees.attributes.consultation_and_treatment_plan_fee : '1999'
                      })}
                    </StyledTreatmentText>
                    <StyledBook
                      onClick={() => window.open(
                        `/en/consultation-flow?doctor_id=${doctor?.attributes?.id || ''}`
                      )}
                      domain={curefyDomain}
                    >
                      <StyledVideoIcon />
                      {t('Doctors:BookNow')}
                    </StyledBook>
                  </StyledPlan>
                </DoctorProcedures>
               ) : (
                 <></>
              )}

              {highlights ? (
                <HighlightsBlock>
                  <Heading>{t('DetailsJob:HighlightTitle')}</Heading>
                  <HighlightsBlockContent domain={curefyDomain}>
                    <StyledList variant="small">
                      {highlights.map(highlight => (
                        <StyledListItem variant="small" key={uuidv4()}>
                          {highlight}
                        </StyledListItem>
                      ))}
                    </StyledList>
                  </HighlightsBlockContent>
                </HighlightsBlock>
              ) : (
                <></>
              )}
            </DoctorLeftBlock>
            <Styledform>
              <FormMini>
                {doctor?.relationships?.consultation_fees?.data.length !== 0
                  ? (
                    <ConsultationForm
                      treatmentPage={title}
                      formId="mobile"
                      isMobile={isMobile}
                      formName="DoctorForm"
                      id="form"
                      doctor_id={doctor_id}
                      department={doctor?.attributes?.department}
                      booking_currency={loadedCurrency}
                      booking_category="second-opinion"
                      curefyDomain={curefyDomain}
                    />
                  ) : (
                    <MiniFlow
                      doctor={typeof window !== 'undefined' && window?.location?.href}
                      treatmentPage={title}
                      formId="mobile"
                      isMobile={isMobile}
                      formName="DoctorForm"
                      id="form"
                      curefyDomain={curefyDomain}
                    />
                  )}
              </FormMini>
            </Styledform>
          </StyledBlock>
          {(() => {
            if (curefyDomain) {
              return <></>;
            }
            return doctors ? (
              packages.length > 0 && (
              <StyledCoverMaxWidthNoBottomMargin>
                <Heading>
                  {t('DetailsJob:TreatmentBold')}
                </Heading>
                {isMobile
                  ? (
                    <StyledPackagesMobile>
                      <ItemsCarousel
                        requestToChangeActive={setPackageSlide}
                        activeItemIndex={packageSlide}
                        numberOfCards={1}
                        gutter={20}
                        alwaysShowChevrons
                        showIndicators
                      >
                        {packages.map(object => (
                          <PackageSearchResultListItem
                            key={object.id}
                            size={{
                              mobile: 1
                            }}
                            result={object}
                            facilityPage
                          />
                        ))}
                      </ItemsCarousel>
                      {packages.length > 1 && (
                      <ProgressBar
                        slide={packageSlide}
                        handleTimeInterval={handlePackageTimeInterval}
                        setSlide={setPackageSlide}
                        intervalId={intervalPackageId}
                        clickable
                        color={color}
                        homePage
                        length={(packages || []).slice(0, 8).length}
                        contributionCard
                      />
                      )}
                      <StyledFlexCenter>
                        <Link passHref href={FINDER_PACKAGE}>
                          <StyledSeeMore>{t('DetailsJob:Viewpackages')}</StyledSeeMore>
                        </Link>
                      </StyledFlexCenter>
                    </StyledPackagesMobile>
                  )
                  : (<DoctorPackages />)}
              </StyledCoverMaxWidthNoBottomMargin>
              )) : <></>;
          })()}

          <DoctorFacilityBlock>
            <DoctorHeading>{t('DetailsJob:DoctorWorksAt')}</DoctorHeading>
            <FacilityBlock>
              <Link
                href={DETAILS_FACILITY}
                params={{ slug: `${facility?.attributes?.slug || ''}` }}
                passHref
              >
                <FacilityLocationBlock>
                  <FacilityName>
                    <FacilityLink>{`${facility?.attributes?.name || ''}`}</FacilityLink>
                    <FacilityLocation>
                      <LocationIconImg />
                      <Location>{doctor?.attributes?.address_city}</Location>
                    </FacilityLocation>
                  </FacilityName>
                </FacilityLocationBlock>
              </Link>

              <FacilityContentBlock>
                <Content>
                  <HighlightContentBlock>
                    {textOverview && (
                    <StyledBulletListWrapper>

                      <StyledRichTextContent dangerouslySetInnerHTML={{ __html: textOverview }} />
                    </StyledBulletListWrapper>
                    )}
                  </HighlightContentBlock>
                  <Link
                    href={DETAILS_FACILITY}
                    params={{ slug: `${facility?.attributes?.slug || ''}` }}
                    passHref
                  >
                    <SeeMoreButton domain={curefyDomain}>{t('DetailsJob:KnowMore')}</SeeMoreButton>
                  </Link>
                </Content>
                <FacilityLocationMobile>
                  <LocationIconImg />
                  <Location>{doctor?.attributes?.address_city}</Location>
                </FacilityLocationMobile>
                <FacilityImage>
                  <FacilityImg src={photoUrl} alt={facility?.attributes?.name} />
                </FacilityImage>
              </FacilityContentBlock>
            </FacilityBlock>
          </DoctorFacilityBlock>

          <HiddenBlock ref={doctorLoadRef} />
          {doctors && doctors.length !== 0 ? (
            <StyledCoverMaxWidthNoBottomMargin>
              <StyledArrowWrapper>
                {doctorFinder.getStatus.requesting ? (
                  <Loader />
                ) : (
                  <>
                    <MainSubHealine>{t('DetailsJob:RelatedDoctors')}</MainSubHealine>
                    {isMobile
                      ? (
                        <>
                          <ItemsCarousel
                            requestToChangeActive={setDoctorsSlide}
                            activeItemIndex={doctorsSlide}
                            numberOfCards={1}
                            gutter={20}
                            alwaysShowChevrons
                            showIndicators
                          >
                            {(doctors || []).slice(0, 8).map(doctor => (
                              <SearchResultListItem
                                key={`result-list-item-${doctor.id}`}
                                result={doctor}
                                loadedOnDetails
                                bookConsultation="hide"
                                size={{
                                  mobile: 1
                                }}
                              />
                            ))}
                          </ItemsCarousel>
                          {doctors.length > 1 && (
                          <ProgressBar
                            slide={doctorsSlide}
                            handleTimeInterval={handleDoctorTimeInterval}
                            setSlide={setDoctorsSlide}
                            intervalId={intervalDoctorId}
                            clickable
                            color={color}
                            homePage
                            length={(doctors || []).slice(0, 8).length}
                            contributionCard
                          />
                          )}
                        </>
                      )
                      : (
                        <GridCarousel
                          length={(doctors || []).slice(0, 8).length}
                          onScreenCount={onScreenCountDoctors}
                          HideArrow={(doctors || []).slice(0, 8).length / onScreenCountDoctors > 1}
                          pagination
                          homePage
                          contributionCard
                        >
                          <>
                            {(doctors || []).slice(0, 8).map(doctor => (
                              <SearchResultListItem
                                key={`result-list-item-${doctor.id}`}
                                result={doctor}
                                loadedOnDetails
                                bookConsultation="hide"
                                size={{
                                  mobile: 1
                          / calculateGridUnitSize((doctors || []).slice(0, 8).length,
                            onScreenCountDoctors)
                                }}
                              />
                            ))}
                          </>
                        </GridCarousel>
                      )}
                  </>
                )}
              </StyledArrowWrapper>
            </StyledCoverMaxWidthNoBottomMargin>
          ) : (
            <></>
          )}
        </StyledWrapper>
      </StyledCover>
    </>
  );
};


DoctorDetails.getInitialProps = async ({ query, store }) => {
  store.dispatch(requestDoctorDetails(query.slug));
  const { getState } = store;
  const { page } = getState();

  const { curefyDomain } = page;

  await waitForPageReady(store);

  return {
    namespacesRequired: ['DetailsJob', 'Doctors'],
    query,
    curefyDomain
  };
};
const mapStateToProps = ({ doctorDetails, doctorFinder, packageFinder }: GlobalState): {} => ({
  doctorDetails,
  doctorFinder,
  packages: packageFinder?.data || [],
  isPackageRequesting: packageFinder?.getStatus.requesting,
});

export default compose(
  withTranslation('DetailsJob', 'Doctors'),
  withFormik({
    reduxErrorKey: 'error',
    reduxInitialValuesKey: 'initialFormValues'
  }),
  connect(
    mapStateToProps,
    {
      requestDoctorDetails,
      resetDoctorDetails,
      requestDoctorList,
      resetDoctorList,
      requestPackageList,
      changeTitle
    }
  )
)(DoctorDetails);
